package com.crowpay.utils

import com.crowpay.Credit
import com.crowpay.JumpSection
import com.crowpay.URLs
import com.crowpay.views.AutoRoutes
import com.lightningkite.UUID
import com.lightningkite.kiteui.AppScope
import com.lightningkite.kiteui.Console
import com.lightningkite.kiteui.ConsoleRoot
import com.lightningkite.kiteui.models.Align
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.Routes
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.views.RView
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.datetime.Instant
import kotlin.time.Duration
import kotlin.time.Duration.Companion.milliseconds

// This is meant to help with scrolling to elements that might be in vastly different areas of the app, and also manage
// actions that need to be executed before scrolling.
object JumpTo {
    private val debug: Console? = null//ConsoleRoot.tag("JumpTo")

    // A data class combining a view to scroll to, and an action to execute before scrolling. Typically used to expand
    // hidden elements before scrolling into view.
    data class Target(val view: RView, val delayBeforeScroll: Duration? = null, val action: ()->Unit) {
        fun go(horizontal: Align? = null, vertical: Align? = Align.Center, animate: Boolean = true) {
            debug?.info("going to target")
            action()
            if (delayBeforeScroll != null) AppScope.launch {
                debug?.info("Delaying scroll")
                delay(delayBeforeScroll)
                debug?.info("scrolling into view")
                view.scrollIntoView(horizontal, vertical, animate)
            }
            else {
                debug?.info("scrolling into view")
                view.scrollIntoView(horizontal, vertical, animate)
            }
        }
    }

    sealed interface Handler : Listenable

    // By the nature of rendering views, targets are late init. This handles the case where you try to navigate to a
    // target that is not rendered yet.
    class TargetHandler(val name: String) : Listenable, Handler {
        // Listeners activate even if no target is currently available. They should ensure a target will render.
        private val listeners = ArrayList<()->Unit>()
        override fun addListener(listener: () -> Unit): () -> Unit {
            debug?.info("Adding listener for $name")
            listeners.add(listener)
            if (awaitingTarget != null) {
                debug?.info("$name Calling listener immediately")
                listener()
            }
            return {
                debug?.info("Removing listener for $name")
                listeners.remove(listener)
            }
        }

        private var target: Target? = null
        private var awaitingTarget: Triple<Align?, Align?, Boolean>? = null

        fun setTarget(view: RView, delayBeforeScroll: Duration? = 500.milliseconds, onScroll: () -> Unit = {}) {
            debug?.info("$name Adding target")
            target = Target(view, delayBeforeScroll, onScroll)
            view.onRemove {
                if (view === target?.view) {
                    debug?.info("$name removing target")
                    target = null
                }
            }
            awaitingTarget?.let { (horizontal, vertical, animate) ->
                debug?.info("$name Fulfilling awaiting target")
                target?.go(horizontal, vertical, animate)
                awaitingTarget = null
            }
        }

        operator fun invoke(horizontal: Align? = null, vertical: Align? = Align.Start, animate: Boolean = true) {
            listeners.invokeAllSafe()
            target
                ?.also { debug?.info("Target found for $name") }
                ?.go(horizontal, vertical, animate)
                ?: run {
                    debug?.info("No target found for $name")
                    awaitingTarget = Triple(horizontal, vertical, animate)
                }
        }
    }

    // Same as above, but manages many targets under a single name
    class TargetCategory<ID>(val name: String) : Listenable, Handler {
        private val listeners = ArrayList<()->Unit>()
        override fun addListener(listener: () -> Unit): () -> Unit {
            debug?.info("Adding listener for $name")
            listeners.add(listener)
            if (awaitingTarget.isNotEmpty()) {
                debug?.info("$name Calling listener immediately")
                listener()
            }
            return {
                listeners.remove(listener)
                debug?.info("Removing listener for $name, ${listeners.size} left")
            }
        }

        private val targets = HashMap<ID, Target>()
        private val awaitingTarget = HashMap<ID, Triple<Align?, Align?, Boolean>>()

        fun newTarget(id: ID, view: RView, delayBeforeScroll: Duration? = 500.milliseconds, onScroll: () -> Unit = {}) {
            val target = Target(view, delayBeforeScroll, onScroll)
            targets[id] = target
            view.onRemove {
                if (view === targets[id]?.view) {
                    debug?.info("Removing target for $name")
                    targets.remove(id)
                }
            }

            awaitingTarget[id]?.let { (horizontal, vertical, animate) ->
                debug?.info("$name fulfilling awaiting target")
                target.go(horizontal, vertical, animate)
                awaitingTarget.remove(id)
            }
        }

        operator fun invoke(id: ID, horizontal: Align? = null, vertical: Align? = Align.Start, animate: Boolean = true) {
            listeners.invokeAllSafe()
            targets[id]
                ?.also { debug?.info("Target found for $name") }
                ?.go(horizontal, vertical, animate)
                ?: run {
                    debug?.info("No target found for $name")
                    awaitingTarget[id] = Triple(horizontal, vertical, animate)
                }
        }
    }

    // Actual Navigators
    val ProjectDetails = TargetHandler("ProjectDetails")
    val ToDos = TargetHandler("ToDos")
    val ProjectMessages = TargetHandler("ProjectMessages")
    val ProjectLedger = TargetHandler("ProjectLedger")

    val PendingChangeOrder = TargetCategory<UUID>("PendingChangeOrder")
    val ChangeItem = TargetCategory<UUID>("ChangeItem")
    val LineItemCompletionFlow = TargetCategory<UUID>("LineItemCompletionFlow")

    val LineItem = TargetCategory<UUID>("LineItem")
    val ScopeView = TargetCategory<UUID>("ScopeView")
    val PayApp = TargetCategory<UUID>("PayApp")

    val ContractorNote = TargetCategory<UUID>("ContractorNote")
    val WorkTask = TargetCategory<UUID>("WorkTask")
    val PunchTask = TargetCategory<UUID>("PunchTask")

    val Credit = TargetCategory<Credit>("Credit")

    // URL handling
    fun jumpFromUrl(parameter: URLs.JumpTo, horizontal: Align? = null, vertical: Align? = Align.Start, animate: Boolean = true) {
        val handler: Handler = when (parameter.section) {
            JumpSection.ProjectDetails -> ProjectDetails
            JumpSection.ToDos -> ToDos
            JumpSection.ProjectMessages -> ProjectMessages
            JumpSection.LineItemCompletionFlow -> LineItemCompletionFlow
            JumpSection.PendingChangeOrder -> PendingChangeOrder
            JumpSection.ChangeItem -> ChangeItem
            JumpSection.LineItem -> LineItem
            JumpSection.ScopeView -> ScopeView
            JumpSection.PayApp -> PayApp
            JumpSection.ContractorNote -> ContractorNote
            JumpSection.WorkTask -> WorkTask
            JumpSection.PunchTask -> PunchTask
        }

        when (handler) {
            is TargetHandler -> handler(horizontal, vertical, animate)
            is TargetCategory<*> -> (handler as TargetCategory<UUID>).invoke(
                parameter.id ?: throw IllegalStateException(if (parameter.section.requiresId) "No Id for JumpTo.${parameter.section}" else "JumpTo.${parameter.section} needs an ID"),
                horizontal, vertical, animate
            )
        }
    }
}